/**
 * useResumeSteps.js
 *
 * Owns step navigation state for the resume builder.
 * Separated from form state so navigation changes never trigger
 * expensive form re-renders and vice-versa.
 */

import { useState, useCallback } from 'react';
import { STEPS } from '../components/resume/resumeConstants';

// Phase labels shown in the header instead of "Step X of 19"
const PHASE_LABELS = {
  1: 'Foundation',
  2: 'Experience',
  3: 'Finish',
};

export function useResumeSteps(validateStep) {
  const [currentStep, setCurrentStep] = useState(0);
  
  console.log('[useResumeSteps] State:', {
    currentStep,
    totalSteps: STEPS.length,
    currentStepId: STEPS[currentStep]?.id,
    allStepIds: STEPS.map(s => s.id)
  });

  const goNext = useCallback(() => {
    const stepId = STEPS[currentStep]?.id;
    console.log('[useResumeSteps] goNext called', { currentStep, stepId });
    if (!validateStep(stepId)) {
      console.warn('[useResumeSteps] Validation failed for step:', stepId);
      return;
    }
    const nextStep = Math.min(currentStep + 1, STEPS.length - 1);
    console.log('[useResumeSteps] Moving to step:', nextStep);
    setCurrentStep(nextStep);
  }, [currentStep, validateStep]);

  const goPrev = useCallback(() => {
    const prevStep = Math.max(0, currentStep - 1);
    console.log('[useResumeSteps] goPrev:', currentStep, '->', prevStep);
    setCurrentStep(prevStep);
  }, [currentStep]);

  const goTo = useCallback((index) => {
    console.log('[useResumeSteps] goTo:', index);
    if (index < 0 || index >= STEPS.length) {
      console.error('[useResumeSteps] Invalid step index:', index, 'max:', STEPS.length - 1);
      return;
    }
    setCurrentStep(index);
  }, []);

  const isFirst    = currentStep === 0;
  const isLast     = currentStep === STEPS.length - 1;
  // Progress based on steps completed — feels faster with 6 steps
  const progress   = Math.round((currentStep / (STEPS.length - 1)) * 100);
  const phase      = STEPS[currentStep]?.phase ?? 1;
  const phaseLabel = PHASE_LABELS[phase] ?? '';
  // Steps in current phase for the phase dot indicator
  const phaseSteps = STEPS.filter(s => s.phase === phase);
  const phaseIndex = phaseSteps.findIndex(s => s.id === STEPS[currentStep]?.id);

  return { currentStep, goNext, goPrev, goTo, isFirst, isLast, progress, phase, phaseLabel, phaseSteps, phaseIndex };
}
